/**
 * useStreamingStateMachine
 *
 * Reusable streaming state machine for AI chat contexts.
 * Tracks lifecycle: IDLE → NETWORK_STREAMING → NETWORK_COMPLETE → DISPLAYING_RESULT → FULLY_COMPLETE
 *
 * Provides state, refs, transition helpers, stop/cancel, and animation-complete handling.
 * Consumers (AILearnContext, RAGChatContext, etc.) call transitionStreamingState at
 * the appropriate points in their own sendMessage flow.
 */

import { useRef, useState, useCallback, useEffect, useMemo } from 'react';

export const STREAMING_STATES = {
  IDLE: 'idle',
  NETWORK_STREAMING: 'network-streaming',
  NETWORK_COMPLETE: 'network-complete',
  DISPLAYING_RESULT: 'displaying-result',
  FULLY_COMPLETE: 'fully-complete',
  ERROR: 'error',
  CANCELLED: 'cancelled',
};

export function useStreamingStateMachine() {
  const streamingStateRef = useRef(STREAMING_STATES.IDLE);
  const [streamingState, setStreamingState] = useState(STREAMING_STATES.IDLE);
  const streamingMessageIndexRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [forceCompleteStreaming, setForceCompleteStreaming] = useState(false);

  // State transition helper with logging
  const transitionStreamingState = useCallback((newState, reason = '') => {
    const prevState = streamingStateRef.current;
    if (prevState !== newState) {
      console.log(`[Streaming] ${prevState} → ${newState}${reason ? ` (${reason})` : ''}`);
      streamingStateRef.current = newState;
      setStreamingState(newState);
    }
  }, []);

  // Cancel streaming on offline
  useEffect(() => {
    const handleOffline = () => {
      if (streamingStateRef.current === STREAMING_STATES.NETWORK_STREAMING) {
        transitionStreamingState(STREAMING_STATES.CANCELLED, 'went offline');
        transitionStreamingState(STREAMING_STATES.FULLY_COMPLETE, 'cancelled recovery');
      }
    };

    window.addEventListener('offline', handleOffline);
    return () => window.removeEventListener('offline', handleOffline);
  }, [transitionStreamingState]);

  // Handle animation completion callback from StreamingMessage
  const handleAnimationComplete = useCallback((messageIndex) => {
    if (streamingMessageIndexRef.current === messageIndex) {
      if (streamingStateRef.current === STREAMING_STATES.NETWORK_COMPLETE) {
        transitionStreamingState(STREAMING_STATES.DISPLAYING_RESULT, `message ${messageIndex}`);
        setTimeout(() => {
          transitionStreamingState(STREAMING_STATES.FULLY_COMPLETE, 'ready for next');
          streamingMessageIndexRef.current = null;
        }, 50);
      }
    }
  }, [transitionStreamingState]);

  // Stop streaming: abort network + freeze animation at current position
  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setForceCompleteStreaming(true);
    transitionStreamingState(STREAMING_STATES.FULLY_COMPLETE, 'user stopped');
  }, [transitionStreamingState]);

  const isStreamingActive = useMemo(() =>
    streamingState === STREAMING_STATES.NETWORK_STREAMING ||
    streamingState === STREAMING_STATES.NETWORK_COMPLETE ||
    streamingState === STREAMING_STATES.DISPLAYING_RESULT,
    [streamingState]
  );

  return {
    STREAMING_STATES,
    streamingState,
    streamingStateRef,
    streamingMessageIndexRef,
    abortControllerRef,
    forceCompleteStreaming,
    setForceCompleteStreaming,
    transitionStreamingState,
    handleAnimationComplete,
    stopStreaming,
    isStreamingActive,
  };
}
